/**
 * Shared drawing of zoning-plan areas: the big map overlay (ZoningPlanLayer) and the map on a town's
 * zoning-plan page (TownZoningPlanMap) style, order, hit-test and describe the areas the same way.
 */
import L from 'leaflet';

export interface ZoningArea {
    readonly layer: string;
    readonly layerName: string;
    readonly code: string | null;
    readonly usageType: string | null;
    readonly usageTypeName: string | null;
    readonly originalLabel: string | null;
    readonly originalColor: string | null;
    readonly areaIndex: string | null;
    readonly planState: string | null;
    readonly planStateName: string | null;
    // Why the area does not apply although the plan draws it (a court or the region annulled it).
    readonly annulment: string | null;
    readonly containedChangeName: string | null;
    readonly containedChangeEffectiveDate: string | null;
    readonly latestChangeName: string | null;
    readonly latestChangeEffectiveDate: string | null;
    readonly behindRegistry: boolean;
    readonly townId: string;
    readonly townName: string | null;
    readonly townRegion: string | null;
    readonly source: string;
    readonly sourceEffectiveDate: string | null;
    readonly vectorizationStatus: string | null;
    readonly vectorizationCoverage: number | null;
    readonly vectorizationParcelScore: number | null;
    readonly vectorizationParcelShift: number | null;
    readonly geometry: GeoJSON.Geometry;
}

// Colours of the land-use mosaic, keyed by LandUsageType value.
export const USAGE_COLORS: Record<string, string> = {
    residential: '#e57373',
    mixed_residential: '#ef9a9a',
    civic_amenities: '#64b5f6',
    recreation: '#ba68c8',
    production: '#9575cd',
    transport_infrastructure: '#90a4ae',
    technical_infrastructure: '#b0bec5',
    public_spaces: '#cfd8dc',
    greenery: '#81c784',
    water: '#4fc3f7',
    agricultural: '#fff176',
    forest: '#388e3c',
    natural: '#aed581',
    mixed_open_landscape: '#c5d6a0',
    mining: '#a1887f',
    specific: '#bcaaa4',
};

export const DEFAULT_USAGE_COLOR = '#cccccc';

// Outline styles for the encumbrance / boundary layers.
export const LAYER_STYLES: Record<string, L.PathOptions> = {
    built_up_area: {color: '#37474f', weight: 2, fill: false, dashArray: '2 4'},
    public_benefit: {color: '#d32f2f', weight: 1.5, dashArray: '5 3', fillColor: '#d32f2f', fillOpacity: 0.1},
    ecological_stability: {color: '#1b5e20', weight: 1.5, dashArray: '6 3', fillColor: '#2e7d32', fillOpacity: 0.12},
    territorial_reserve: {color: '#6d4c41', weight: 1.5, dashArray: '8 4', fillColor: '#8d6e63', fillOpacity: 0.1},
    corridor: {color: '#c2185b', weight: 1.5, dashArray: '10 3 2 3', fillColor: '#ec407a', fillOpacity: 0.1},
};

// An area a court or the region annulled: drawn grey and dotted, so it is told from the areas that apply.
export const ANNULLED_COLOR = '#616161';
export const ANNULLED_LEGEND_LABEL = 'Zrušeno soudem nebo krajským úřadem';
export const ANNULLED_EXPLANATION = 'Tato část územního plánu neplatí.';

export function isAnnulled(area: {readonly annulment: string | null}): boolean {
    return area.annulment !== null;
}

// The style of an area of an overlay layer; an annulled one grey.
export function overlayStyle(area: Pick<ZoningArea, 'layer' | 'annulment'>): L.PathOptions {
    const style = LAYER_STYLES[area.layer] ?? {};
    return isAnnulled(area) ? {...style, color: ANNULLED_COLOR, fillColor: ANNULLED_COLOR, dashArray: '2 3'} : style;
}

// The plan's own colour as drawn in the document (1:1) takes precedence; only plans without a stored
// original colour (regional vector imports) fall back to the unified palette derived from usageType.
export function fillColorOf(area: ZoningArea): string {
    return area.originalColor ?? USAGE_COLORS[area.usageType ?? ''] ?? DEFAULT_USAGE_COLOR;
}

// The unified colour of the area's usage type, whatever colour its own plan gives it; the map of the
// zoning plan module compares the towns in these colours.
export function unifiedColorOf(area: Pick<ZoningArea, 'usageType'>): string {
    return USAGE_COLORS[area.usageType ?? ''] ?? DEFAULT_USAGE_COLOR;
}

// Bounding-box area of a geometry (in squared degrees) — a cheap proxy for polygon size, used to order
// areas so big background polygons render beneath the small specific ones.
export function geometryBboxArea(geometry: GeoJSON.Geometry): number {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    const visit = (coords: unknown): void => {
        if (typeof (coords as number[])[0] === 'number') {
            const [x, y] = coords as [number, number];
            minX = Math.min(minX, x);
            minY = Math.min(minY, y);
            maxX = Math.max(maxX, x);
            maxY = Math.max(maxY, y);
            return;
        }
        for (const child of coords as unknown[]) {
            visit(child);
        }
    };
    if ('coordinates' in geometry) {
        visit(geometry.coordinates);
    }
    if (minX === Infinity) {
        return 0;
    }
    return (maxX - minX) * (maxY - minY);
}

export function landUseStyle(area: ZoningArea): L.PathOptions {
    if (isAnnulled(area)) {
        return {color: ANNULLED_COLOR, weight: 1.2, fillColor: fillColorOf(area), fillOpacity: 0.15, dashArray: '2 3'};
    }
    return {
        color: '#555555',
        weight: 0.6,
        fillColor: fillColorOf(area),
        fillOpacity: 0.5,
        dashArray: area.planState === 'proposed' ? '4 3' : undefined,
    };
}

export function formatDate(isoDate: string): string {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
    if (!match) {
        return isoDate;
    }
    return `${Number(match[3])}. ${Number(match[2])}. ${match[1]}`;
}

// How current a town's map is, as the registries (EÚPČ, NGÚP) confirm it — the backend never sends a
// source's own date as the plan's (ZoningPlanGeometryCurrencyResolver).
export interface PlanCurrency {
    readonly containedChangeName: string | null;
    readonly containedChangeEffectiveDate: string | null;
    readonly latestChangeName: string | null;
    readonly latestChangeEffectiveDate: string | null;
    readonly behindRegistry: boolean;
}

export interface PlanCurrencyTexts {
    // The registered version the map contains.
    readonly state: string | null;
    // A newer registered version the map lacks.
    readonly warning: string | null;
    // The newest registered version when it is not known which one the map contains.
    readonly info: string | null;
}

function changeLabel(name: string | null, effectiveDate: string): string {
    const date = `účinnost od ${formatDate(effectiveDate)}`;
    return name ? `${name} (${date})` : date;
}

export function planCurrencyTexts(currency: PlanCurrency): PlanCurrencyTexts {
    const latest = currency.latestChangeEffectiveDate !== null
        ? changeLabel(currency.latestChangeName, currency.latestChangeEffectiveDate)
        : null;

    if (currency.containedChangeEffectiveDate === null) {
        return {
            state: null,
            warning: null,
            info: latest !== null ? `Poslední účinná změna podle registru: ${latest}. Zda ji mapa obsahuje, nemáme ověřené.` : null,
        };
    }

    return {
        state: `Stav plánu: ${changeLabel(currency.containedChangeName, currency.containedChangeEffectiveDate)}`,
        warning: currency.behindRegistry && latest !== null ? `Novější účinná změna: ${latest} — mapa ji zatím neobsahuje.` : null,
        info: null,
    };
}

// Human names of the import sources; anything unmapped (and the technical ids) stays admin-only.
const SOURCE_LABELS: Record<string, string> = {
    'zlinsky-kraj': 'data Zlínského kraje',
    'vysocina-pupo': 'data Kraje Vysočina',
    'ustecky-zfp': 'data Ústeckého kraje',
    'ustecky-uap': 'data Ústeckého kraje (ÚAP)',
    ngup: 'národní geoportál (NGÚP)',
    'std:municipal': 'strojová data obce',
    'praha-ipr': 'data IPR Praha',
    'brno-wfs': 'data města Brna',
    'usti-up': 'data města Ústí nad Labem',
    'ostrava-up': 'data města Ostravy',
    'olomouc-up': 'data města Olomouce',
    'plzen-ukr': 'data města Plzně',
    'most-up': 'data města Mostu',
};

export function sourceLabel(source: string): string {
    if (source === 'pdf' || source.startsWith('pdf:')) {
        return 'vektorizace výkresu ÚP';
    }
    return SOURCE_LABELS[source] ?? source;
}

// Where the area comes from — municipality (with region) plus the import source. The municipality
// name links to the town's zoning-plan detail page, where the plan's documents can be browsed and
// downloaded. Admins additionally get the technical town id and raw source, so a mislabelled area
// can be traced to its pipeline without opening the database.
function originLine(area: ZoningArea, planDetailUrl: string, debugInfo: boolean): string {
    const town = area.townName
        ? `${escapeHtml(area.townName)}${area.townRegion ? ` (${escapeHtml(area.townRegion)})` : ''}`
        : escapeHtml(area.townId);
    const url = planDetailUrl.replace('__TOWN__', encodeURIComponent(area.townId));
    const link = `<a class="zoning-plan-popup__link" href="${escapeHtml(url)}" target="_blank" rel="noopener">${town}</a>`;
    const parts = [link, escapeHtml(sourceLabel(area.source))];
    if (debugInfo) {
        parts.push(`${escapeHtml(area.townId)} · ${escapeHtml(area.source)}`);
    }
    return `<small class="zoning-plan-popup__meta">${parts.join(' · ')}</small>`;
}

// Admin-only vectorization quality of the town's PDF import — map-side QA without the database.
function diagnosticsLine(area: ZoningArea): string | null {
    if (!area.vectorizationStatus) {
        return null;
    }
    const parts = [`stav ${escapeHtml(area.vectorizationStatus)}`];
    if (area.vectorizationCoverage !== null) {
        parts.push(`pokrytí ${area.vectorizationCoverage} %`);
    }
    if (area.vectorizationParcelScore !== null) {
        parts.push(`parcely ${area.vectorizationParcelScore} %`);
    }
    if (area.vectorizationParcelShift !== null) {
        parts.push(`posun ${area.vectorizationParcelShift} m`);
    }
    return `<small class="zoning-plan-popup__meta">Vektorizace: ${parts.join(' · ')}</small>`;
}

function colorSwatch(color: string): string {
    return `<span class="zoning-plan-popup__swatch" style="background:${color}"></span>`;
}

export function escapeHtml(value: string): string {
    return value.replace(/[&<>"]/g, (character) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
    })[character] ?? character);
}

// Regional sources (Vysočina PUPO) store the plan's whole regulation text as the label — up to
// tens of kilobytes. Anything beyond a short legend row moves into a scrollable block, otherwise
// the popup grows past the viewport with no way to scroll it.
const LONG_LABEL_THRESHOLD = 140;

export function isLongLabel(label: string): boolean {
    return label.length > LONG_LABEL_THRESHOLD || label.includes('\n');
}

export function popupContent(area: ZoningArea, planDetailUrl: string, debugInfo: boolean): string {
    if (area.layer === 'land_use') {
        const color = fillColorOf(area);
        const label = area.originalLabel;
        const longLabel = label !== null && isLongLabel(label);
        // heading shows the plan's OWN legend (code + original description) 1:1; the unified usageType
        // name is the heading for sources without an original label — or with an over-long one
        const code = area.code ? `${escapeHtml(area.code)} — ` : '';
        const heading = label && !longLabel
            ? `${code}${escapeHtml(label)}`
            : `${code}${escapeHtml(area.usageTypeName ?? 'Plocha')}`;
        const lines = [`${colorSwatch(color)}<strong>${heading}</strong>`];
        if (area.annulment !== null) {
            lines.push(annulmentLine(area.annulment));
        }
        // the unified category as a small secondary line, only when we also showed the original label
        if (label && !longLabel && area.usageTypeName) {
            lines.push(`<small class="zoning-plan-popup__meta">${escapeHtml(area.usageTypeName)}</small>`);
        } else if (!label && area.code) {
            lines.push(`Kód: ${escapeHtml(area.code)}`);
        }
        if (label && longLabel) {
            lines.push(`<div class="zoning-plan-popup__label">${escapeHtml(label)}</div>`);
        }
        if (area.areaIndex) {
            lines.push(`<small class="zoning-plan-popup__meta">Míra využití: ${escapeHtml(area.areaIndex)}</small>`);
        }
        if (area.planStateName) {
            lines.push(escapeHtml(area.planStateName));
        }
        const currency = planCurrencyTexts(area);
        if (currency.state) {
            lines.push(`<small class="zoning-plan-popup__meta">${escapeHtml(currency.state)}</small>`);
        }
        if (currency.warning) {
            lines.push(`<small class="zoning-plan-popup__meta">⚠ ${escapeHtml(currency.warning)}</small>`);
        }
        if (currency.info) {
            lines.push(`<small class="zoning-plan-popup__meta">${escapeHtml(currency.info)}</small>`);
        }
        if (debugInfo) {
            // Admin-only: the date the source gives, which users never see unless a registry confirms it.
            if (area.sourceEffectiveDate) {
                const confirmation = area.containedChangeEffectiveDate ? 'potvrzené registrem' : 'registr ho nepotvrzuje';
                lines.push(`<small class="zoning-plan-popup__meta">Datum ze zdroje: ${formatDate(area.sourceEffectiveDate)} (${confirmation})</small>`);
            }
            const diagnostics = diagnosticsLine(area);
            if (diagnostics) {
                lines.push(diagnostics);
            }
        }
        lines.push(originLine(area, planDetailUrl, debugInfo));
        return lines.join('<br/>');
    }

    const swatch = colorSwatch((overlayStyle(area).color as string | undefined) ?? DEFAULT_USAGE_COLOR);
    const designation = [area.code, area.areaIndex]
        .filter((value): value is string => Boolean(value))
        .map(escapeHtml)
        .join(' ');
    const annulment = area.annulment !== null ? `<br/>${annulmentLine(area.annulment)}` : '';
    return `${swatch}<strong>${escapeHtml(area.layerName)}</strong>${designation ? `<br/>${designation}` : ''}${annulment}<br/>${originLine(area, planDetailUrl, debugInfo)}`;
}

function annulmentLine(annulment: string): string {
    return `<span class="text-danger fw-semibold">${escapeHtml(annulment)}</span><br/>${escapeHtml(ANNULLED_EXPLANATION)}`;
}

function isPointInLayer(map: L.Map, layer: L.Layer, latlng: L.LatLng): boolean {
    const point = map.latLngToLayerPoint(latlng);
    const path = layer as unknown as {_containsPoint?: (p: L.Point) => boolean};
    return path._containsPoint !== undefined && path._containsPoint(point);
}

// pick the SMALLEST area containing the point: plans draw large background areas (e.g.
// "veřejná prostranství" under a whole built-up zone) beneath the specific blocks (BI, OV),
// so the smallest one at the click is the relevant, most specific area — not the first.
// An area that applies goes before an annulled one, as ZoningPlanAtPointQueryHandler orders them:
// the card tells what applies at the point. Areas the predicate rejects (hidden on the map) are never hit.
export function findHitFeature<Area = ZoningArea>(
    map: L.Map,
    geoJson: L.GeoJSON,
    latlng: L.LatLng,
    isHittable: (area: Area) => boolean = () => true,
): GeoJSON.Feature | null {
    let hit: GeoJSON.Feature | null = null;
    let hitApplies = false;
    let smallest = Infinity;
    geoJson.eachLayer((layer) => {
        const path = layer as L.Layer & {feature?: GeoJSON.Feature; getBounds?: () => L.LatLngBounds};
        if (path.getBounds === undefined || ! path.getBounds().contains(latlng) || ! isPointInLayer(map, layer, latlng)) {
            return;
        }
        if (path.feature?.properties && ! isHittable(path.feature.properties as Area)) {
            return;
        }
        const bounds = path.getBounds();
        const area = (bounds.getEast() - bounds.getWest()) * (bounds.getNorth() - bounds.getSouth());
        // The features of other layers (towns) carry no annulment: they all apply.
        const applies = typeof path.feature?.properties?.annulment !== 'string';
        if ((applies && ! hitApplies) || (applies === hitApplies && area < smallest)) {
            smallest = area;
            hitApplies = applies;
            hit = path.feature ?? null;
        }
    });
    return hit;
}

// Loads the areas of one layer in the bounds; townId limits them to a single town. Returns null when
// the server refuses the request.
export async function fetchAreas(
    loadAreasUrl: string,
    layerKey: string,
    bounds: L.LatLngBounds,
    signal: AbortSignal,
    townId: string | null = null,
): Promise<ZoningArea[] | null> {
    const response = await fetch(loadAreasUrl.replace('__LAYER__', encodeURIComponent(layerKey)), {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
            boundingBox: {
                southWest: {longitude: bounds.getWest(), latitude: bounds.getSouth()},
                northEast: {longitude: bounds.getEast(), latitude: bounds.getNorth()},
            },
            ...(townId !== null ? {townId} : {}),
        }),
        signal,
    });

    if (!response.ok) {
        return null;
    }

    return await response.json();
}

// draw large areas first (underneath) and small ones last (on top): a plan's big
// background area (e.g. "veřejná prostranství") must not hide the specific blocks above it
export function toFeatureCollection(areas: ReadonlyArray<Pick<ZoningArea, 'geometry'>>): GeoJSON.FeatureCollection {
    const ordered = [...areas].sort((a, b) => geometryBboxArea(b.geometry) - geometryBboxArea(a.geometry));

    return {
        type: 'FeatureCollection',
        features: ordered.map((area) => ({
            type: 'Feature',
            geometry: area.geometry,
            properties: area,
        })),
    };
}
